/**
 * Channel Profitability Page - Track channel revenue and costs
 */
import { useState, useEffect } from 'react';
import { 
  Users, DollarSign, TrendingUp, TrendingDown, Plus, Edit2, Trash2, Save,
  AlertTriangle, CheckCircle, XCircle, Building2, Mail, Phone, Calendar,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Filter
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

// Default channels list
const DEFAULT_CHANNELS = [
  { name: 'Fira Local (FTC)', industry: 'Trading' },
  { name: 'Goldcast (FGC)', industry: 'Trading' },
  { name: 'MBE International', industry: 'International' },
  { name: 'Ciko Supercalper', industry: 'Trading' },
  { name: 'Revolusi Nusatrade', industry: 'Trading' },
  { name: 'Sahara AI', industry: 'Technology' },
  { name: 'MSE Ustad', industry: 'Education' },
  { name: 'Garuda Global', industry: 'International' },
  { name: 'Padei', industry: 'Trading' }
];

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const ChannelProfitabilityPage = () => {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddRevenue, setShowAddRevenue] = useState(false);
  const [showAddCost, setShowAddCost] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', industry: '' });
  const [revenueForm, setRevenueForm] = useState({ client_id: '', amount: '', date: '', description: '', project: '' });
  const [costForm, setCostForm] = useState({ client_id: '', amount: '', date: '', description: '', category: '' });

  const fetchData = async () => {
    try {
      const [profitRes, clientsRes] = await Promise.all([
        axios.get(`${API}/clients/profitability?period=${period}`),
        axios.get(`${API}/clients`)
      ]);
      setData(profitRes.data);
      setClients(clientsRes.data);
      
      // Auto-add default channels only if no clients exist (first time only)
      if (clientsRes.data.length === 0 && !localStorage.getItem('channels_initialized')) {
        await initializeDefaultChannels();
        localStorage.setItem('channels_initialized', 'true');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultChannels = async () => {
    try {
      for (const channel of DEFAULT_CHANNELS) {
        await axios.post(`${API}/clients`, {
          name: channel.name,
          industry: channel.industry,
          status: 'active'
        });
      }
      // Refetch data after adding channels
      const [profitRes, clientsRes] = await Promise.all([
        axios.get(`${API}/clients/profitability?period=${period}`),
        axios.get(`${API}/clients`)
      ]);
      setData(profitRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error initializing channels:', error);
    }
  };

  useEffect(() => { fetchData(); }, [period]);

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients`, form);
      setShowAddClient(false);
      setForm({ name: '', contact_person: '', email: '', phone: '', industry: '' });
      fetchData();
    } catch (error) {
      alert('Error adding channel');
    }
  };

  const handleAddRevenue = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients/revenue`, { ...revenueForm, amount: parseFloat(revenueForm.amount) });
      setShowAddRevenue(false);
      setRevenueForm({ client_id: '', amount: '', date: '', description: '', project: '' });
      fetchData();
    } catch (error) {
      alert('Error adding revenue');
    }
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients/cost`, { ...costForm, amount: parseFloat(costForm.amount) });
      setShowAddCost(false);
      setCostForm({ client_id: '', amount: '', date: '', description: '', category: '' });
      fetchData();
    } catch (error) {
      alert('Error adding cost');
    }
  };

  const handleDeleteClient = async (id) => {
    if (window.confirm('Padam channel ini dan semua data berkaitan?')) {
      await axios.delete(`${API}/clients/${id}`);
      fetchData();
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-b-2 border-red-700 rounded-full"></div></div>;

  const summary = data?.summary || {};
  const pieData = [
    { name: 'Profitable', value: summary.profitable_count || 0, color: '#10B981' },
    { name: 'Low Margin', value: summary.low_margin_count || 0, color: '#F59E0B' },
    { name: 'Losing', value: summary.losing_count || 0, color: '#EF4444' },
    { name: 'Inactive', value: summary.inactive_count || 0, color: '#9CA3AF' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="channel-profitability-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Building2 className="w-8 h-8" /> Channel Profitability
            </h1>
            <p className="text-blue-200 text-sm mt-1">Track which IB channels are making you money</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button 
              onClick={async () => {
                try {
                  await axios.post(`${API}/clients/sync-from-revenue`);
                  await axios.post(`${API}/clients/sync-advertising-costs`);
                  fetchData();
                  alert('Sync completed!');
                } catch (e) {
                  alert('Sync failed');
                }
              }}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" /> Sync Revenue Data
            </button>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 bg-white/20 rounded-lg text-white text-sm">
              <option value="all" className="text-gray-800">All Time</option>
              <option value="month" className="text-gray-800">This Month</option>
              <option value="quarter" className="text-gray-800">This Quarter</option>
              <option value="year" className="text-gray-800">This Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-500">Total Channels</p>
          <p className="text-2xl font-bold text-gray-800">{summary.total_clients || 0}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-xs text-emerald-600">Total Revenue</p>
          <p className="text-2xl font-bold text-emerald-700">RM{(summary.total_revenue || 0).toLocaleString()}</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
          <p className="text-xs text-red-600">Total Cost</p>
          <p className="text-2xl font-bold text-red-700">RM{(summary.total_cost || 0).toLocaleString()}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-xs text-blue-600">Total Profit</p>
          <p className="text-2xl font-bold text-blue-700">RM{(summary.total_profit || 0).toLocaleString()}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
          <p className="text-xs text-amber-600">Avg Margin</p>
          <p className="text-2xl font-bold text-amber-700">{summary.avg_margin || 0}%</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button onClick={() => setShowAddClient(true)} className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Tambah Channel
        </button>
        <button onClick={() => setShowAddRevenue(true)} className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 flex items-center gap-2 text-sm">
          <ArrowUpRight className="w-4 h-4" /> Tambah Revenue
        </button>
        <button onClick={() => setShowAddCost(true)} className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 text-sm">
          <ArrowDownRight className="w-4 h-4" /> Tambah Cost
        </button>
      </div>

      {/* Charts & Top/Bottom */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4">Channel Distribution</h3>
          <div className="h-64">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {pieData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p>Tambah revenue/cost untuk lihat distribution</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Channels */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> Top 5 Profitable Channels
          </h3>
          <div className="space-y-3">
            {(data?.top_clients || []).filter(c => c.profit > 0).slice(0, 5).map((client, idx) => (
              <div key={client.client_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                  <span className="font-medium text-gray-800">{client.client_name}</span>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-600">RM{client.profit?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{client.margin}% margin</p>
                </div>
              </div>
            ))}
            {(!data?.top_clients || data.top_clients.filter(c => c.profit > 0).length === 0) && (
              <p className="text-gray-400 text-center py-4">Tambah revenue untuk lihat &quot;top channels&quot;</p>
            )}
          </div>
        </div>
      </div>

      {/* Losing Channels Alert */}
      {data?.bottom_clients?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="font-bold text-red-700 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Channels Yang Rugi - Perlu Perhatian!
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.bottom_clients.map(client => (
              <div key={client.client_id} className="bg-white rounded-lg p-4 border border-red-200">
                <p className="font-semibold text-gray-800">{client.client_name}</p>
                <p className="text-sm text-gray-500">{client.industry || 'No industry'}</p>
                <div className="mt-2 flex justify-between">
                  <span className="text-xs text-gray-400">Revenue: RM{client.revenue?.toLocaleString()}</span>
                  <span className="text-xs text-gray-400">Cost: RM{client.cost?.toLocaleString()}</span>
                </div>
                <p className="text-red-600 font-bold mt-1">Loss: RM{Math.abs(client.profit)?.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Channels Table */}
      <div className="bg-white rounded-xl p-6 border shadow-sm">
        <h3 className="font-bold text-gray-800 mb-4">All Channels</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2">Channel</th>
                <th className="text-left py-3 px-2">Industry</th>
                <th className="text-right py-3 px-2">Revenue</th>
                <th className="text-right py-3 px-2">Cost</th>
                <th className="text-right py-3 px-2">Profit</th>
                <th className="text-right py-3 px-2">Margin</th>
                <th className="text-center py-3 px-2">Status</th>
                <th className="text-center py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.all_clients || []).map(client => (
                <tr key={client.client_id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium">{client.client_name}</td>
                  <td className="py-3 px-2 text-gray-500">{client.industry || '-'}</td>
                  <td className="py-3 px-2 text-right text-emerald-600">RM{client.revenue?.toLocaleString()}</td>
                  <td className="py-3 px-2 text-right text-red-600">RM{client.cost?.toLocaleString()}</td>
                  <td className={`py-3 px-2 text-right font-bold ${client.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    RM{client.profit?.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-right">{client.margin}%</td>
                  <td className="py-3 px-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      client.profit_status === 'highly_profitable' ? 'bg-emerald-100 text-emerald-700' :
                      client.profit_status === 'profitable' ? 'bg-blue-100 text-blue-700' :
                      client.profit_status === 'low_margin' ? 'bg-amber-100 text-amber-700' :
                      client.profit_status === 'losing_money' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {client.profit_status === 'no_activity' ? 'No Activity' : client.profit_status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <button onClick={() => handleDeleteClient(client.client_id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Channel Modal */}
      <Modal isOpen={showAddClient} onClose={() => setShowAddClient(false)} title="Tambah Channel Baru">
        <form onSubmit={handleAddClient} className="space-y-4">
          <input type="text" placeholder="Nama Channel *" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="text" placeholder="Contact Person" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="text" placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="text" placeholder="Industry" value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <button type="submit" className="w-full py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800">Simpan</button>
        </form>
      </Modal>

      {/* Add Revenue Modal */}
      <Modal isOpen={showAddRevenue} onClose={() => setShowAddRevenue(false)} title="Tambah Revenue">
        <form onSubmit={handleAddRevenue} className="space-y-4">
          <select required value={revenueForm.client_id} onChange={(e) => setRevenueForm({...revenueForm, client_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
            <option value="">Pilih Channel *</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="number" placeholder="Amount (RM) *" required value={revenueForm.amount} onChange={(e) => setRevenueForm({...revenueForm, amount: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="date" required value={revenueForm.date} onChange={(e) => setRevenueForm({...revenueForm, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="text" placeholder="Description" value={revenueForm.description} onChange={(e) => setRevenueForm({...revenueForm, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <button type="submit" className="w-full py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800">Simpan</button>
        </form>
      </Modal>

      {/* Add Cost Modal */}
      <Modal isOpen={showAddCost} onClose={() => setShowAddCost(false)} title="Tambah Cost">
        <form onSubmit={handleAddCost} className="space-y-4">
          <select required value={costForm.client_id} onChange={(e) => setCostForm({...costForm, client_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
            <option value="">Pilih Channel *</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="number" placeholder="Amount (RM) *" required value={costForm.amount} onChange={(e) => setCostForm({...costForm, amount: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="date" required value={costForm.date} onChange={(e) => setCostForm({...costForm, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <input type="text" placeholder="Description" value={costForm.description} onChange={(e) => setCostForm({...costForm, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          <select value={costForm.category} onChange={(e) => setCostForm({...costForm, category: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
            <option value="">Select Category</option>
            <option value="commission">Commission</option>
            <option value="marketing">Marketing</option>
            <option value="operations">Operations</option>
            <option value="other">Other</option>
          </select>
          <button type="submit" className="w-full py-2 bg-red-700 text-white rounded-lg hover:bg-red-800">Simpan</button>
        </form>
      </Modal>
    </div>
  );
};

export default ChannelProfitabilityPage;
